import { Loader2 } from 'lucide-react';
import { useTranslations } from 'use-intl';
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { BankAccountSummary } from '@/types/accounts';
import { formatAmount } from "@financialharmony/ui";

/**
 * How the last "Update access" attempt ended (FIN-863), shown above the list:
 * - `accessNotUpdated`: the bank did not confirm the new authorization.
 * - `relinkFailed`: the bank approved, but moving the connected accounts onto it failed.
 * - `listFailed`: access was updated, but loading the account list afterwards failed.
 * - `noNewAccounts`: it worked, but the bank shared nothing beyond the connected accounts.
 */
export type UpdateAccessOutcome = 'none' | 'accessNotUpdated' | 'relinkFailed' | 'listFailed' | 'noNewAccounts';

interface BankAccountsToConnectTableProps {
  accounts: BankAccountSummary[];
  connectingAccountId: string | null;
  isConnectingAccount: boolean;
  onConnect: (accountId: string) => void;
  /** Only on a list served by a reused consent, where the bank was never asked again. */
  showUpdateAccessLink: boolean;
  onUpdateAccess: () => void;
  updateOutcome: UpdateAccessOutcome;
  onRetryUpdate: () => void;
  onRetryRelink: () => void;
  isRetryingRelink: boolean;
}

function UpdateOutcomeAlert({
  outcome, onRetryUpdate, onRetryRelink, isRetryingRelink,
}: Pick<BankAccountsToConnectTableProps, 'onRetryUpdate' | 'onRetryRelink' | 'isRetryingRelink'> & { outcome: UpdateAccessOutcome }) {
  const t = useTranslations('consultantBank.connect.table');
  if (outcome === 'none') return null;
  // Both finish the update without another bank authorization; the relink is idempotent.
  const isRelink = outcome === 'relinkFailed' || outcome === 'listFailed';
  const destructive = outcome !== 'noNewAccounts';
  return (
    <Alert variant={destructive ? 'destructive' : 'default'}>
      <AlertTitle>{t(`outcomes.${outcome}.title`)}</AlertTitle>
      <AlertDescription className="flex flex-col gap-2">
        <span>{t(`outcomes.${outcome}.body`)}</span>
        <Button
          variant="outline"
          size="sm"
          className="self-start"
          onClick={isRelink ? onRetryRelink : onRetryUpdate}
          disabled={isRelink && isRetryingRelink}
        >
          {isRelink && isRetryingRelink ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              {t('retrying')}
            </>
          ) : t('tryAgain')}
        </Button>
      </AlertDescription>
    </Alert>
  );
}

/** The consultant's "Select account to connect" step: connectable rows, already-connected rows,
 *  and the FIN-863 Update access affordances. See specs/bank-reconsent-additional-accounts/screens.md. */
export function BankAccountsToConnectTable({
  accounts,
  connectingAccountId,
  isConnectingAccount,
  onConnect,
  showUpdateAccessLink,
  onUpdateAccess,
  updateOutcome,
  onRetryUpdate,
  onRetryRelink,
  isRetryingRelink,
}: BankAccountsToConnectTableProps) {
  const t = useTranslations('consultantBank');
  const tTable = useTranslations('consultantBank.connect.table');
  return (
    <div className="space-y-4">
      <UpdateOutcomeAlert
        outcome={updateOutcome}
        onRetryUpdate={onRetryUpdate}
        onRetryRelink={onRetryRelink}
        isRetryingRelink={isRetryingRelink}
      />

      {accounts.length > 0 ? (
        <div className="max-h-[300px] overflow-y-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{tTable('account')}</TableHead>
                <TableHead>{tTable('balance')}</TableHead>
                <TableHead className="w-[100px]">{tTable('connect')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {accounts.map((account) => (
                <TableRow key={account.id}>
                  <TableCell>
                    <div>
                      <p className="font-medium">{account.bankName}</p>
                      <p className="text-sm text-muted-foreground">{account.maskedNumber ?? t('noAccountNumber')}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <p className="font-medium">{account.balance != null ? formatAmount(account.balance) : ''} {account.currency}</p>
                  </TableCell>
                  <TableCell>
                    {account.alreadyConnected ? (
                      <Badge variant="secondary">{tTable('alreadyConnected')}</Badge>
                    ) : (
                      <Button
                        size="sm"
                        onClick={() => onConnect(account.id)}
                        disabled={isConnectingAccount}
                        className="w-full"
                      >
                        {isConnectingAccount && connectingAccountId === account.id ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                            {tTable('connecting')}
                          </>
                        ) : (
                          tTable('connect')
                        )}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <div className="text-center py-8">
          <p className="text-muted-foreground mb-4">
            {tTable('noNewFound')}
          </p>
        </div>
      )}

      {showUpdateAccessLink && (
        <p className="text-sm text-muted-foreground text-center">
          {tTable('missingAccounts')}{' '}
          <button
            type="button"
            onClick={onUpdateAccess}
            disabled={isConnectingAccount}
            className="font-medium text-primary underline underline-offset-2 hover:text-primary/80 disabled:opacity-50 disabled:pointer-events-none"
          >
            {tTable('updateAccess')}
          </button>
        </p>
      )}
    </div>
  );
}
